import React from "react";
import PropTypes from "prop-types";

/**
 * Shared editor controls for the patient sections, so the archetype's form
 * primitives live in one place rather than drifting per section. Tokens read
 * from the Chief complaints / Diagnosis editors (Figma) via Dev Mode MCP.
 */

/**
 * A coloured status chip (table cells that are inline-editable dropdowns in the
 * design). `band` is { label, bg, fg, dot? }. `small` = the h22/radius-4 variant
 * (treatment/medicine/allergy); default = h26/radius-6 (VAS). UI-only: rendered
 * as a button but the menu isn't wired.
 */
export const StatusChip = ({ band, small }) => (
  <button
    type="button"
    className={`status-chip${small ? " is-small" : ""}`}
    style={{ background: band.bg, color: band.fg }}
    title="Editing status isn't wired up yet"
  >
    {band.dot ? (
      <span className="status-chip-dot" style={{ background: band.dot }} aria-hidden="true" />
    ) : null}
    <span className="status-chip-label">{band.label}</span>
    <span className="status-chip-caret" aria-hidden="true">
      &#9662;
    </span>
  </button>
);
StatusChip.propTypes = {
  band: PropTypes.shape({
    label: PropTypes.string.isRequired,
    bg: PropTypes.string.isRequired,
    fg: PropTypes.string.isRequired,
    dot: PropTypes.string,
  }).isRequired,
  small: PropTypes.bool,
};

/**
 * A segmented button strip (dose / frequency / duration / severity pickers).
 * `options` is [{ value, label }] or [string]. `multi` toggles multiple; single
 * otherwise. Renders as a bordered strip that wraps across rows. UI-only.
 */
export const SegStrip = ({ options, value, onChange, multi }) => {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const selected = multi ? value || [] : value;
  const isOn = (v) => (multi ? selected.indexOf(v) !== -1 : selected === v);
  const pick = (v) => {
    if (!multi) return onChange(v);
    return onChange(isOn(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };
  return (
    <div className="seg-strip">
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`seg-cell${isOn(o.value) ? " is-on" : ""}`}
          aria-pressed={isOn(o.value)}
          onClick={() => pick(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};
SegStrip.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  multi: PropTypes.bool,
};

/**
 * Toggle pill-chip group (Lifestyle sections). ON = green border + check, OFF =
 * grey border. `multi` toggles a set; single otherwise. `options` is [string] or
 * [{ value, label, ai }] where `ai` marks an AI-suggested pick. UI-only.
 */
export const ChipGroup = ({ options, value, onChange, multi }) => {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const sel = multi ? value || [] : value;
  const on = (v) => (multi ? sel.indexOf(v) !== -1 : sel === v);
  const pick = (v) => {
    if (!multi) return onChange(sel === v ? null : v);
    return onChange(on(v) ? sel.filter((x) => x !== v) : [...sel, v]);
  };
  return (
    <div className="chip-group">
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`chip-toggle${on(o.value) ? " is-on" : ""}`}
          aria-pressed={on(o.value)}
          onClick={() => pick(o.value)}
        >
          {on(o.value) ? (
            <svg className="chip-check" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : null}
          <span>{o.label}</span>
          {o.ai ? <span className="ai-badge is-inline">AI</span> : null}
        </button>
      ))}
    </div>
  );
};
ChipGroup.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  multi: PropTypes.bool,
};

/**
 * The AI sparkle glyph, as a bare icon. Single source of truth for the mark —
 * AiBadge/AiCallout wrap it, and the AI-transcript modal uses it directly. Pass a
 * className to size/colour it via CSS (fill: currentColor).
 */
export const Sparkle = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M10 2l1.6 4.4L16 8l-4.4 1.6L10 14l-1.6-4.4L4 8l4.4-1.6L10 2z" />
  </svg>
);
Sparkle.propTypes = { className: PropTypes.string };

/** AI provenance badge. variant: "drafted" (purple) or "tailored" (gold gradient). */
export const AiBadge = ({ variant, children }) => (
  <span className={`ai-badge${variant === "tailored" ? " is-tailored" : ""}`}>
    {variant !== "tailored" ? <Sparkle /> : null}
    {children}
  </span>
);
AiBadge.propTypes = { variant: PropTypes.string, children: PropTypes.node };

/** Purple AI-summary callout box (sparkle + eyebrow + body). */
export const AiCallout = ({ eyebrow, children }) => (
  <div className="ai-callout">
    <Sparkle className="ai-callout-icon" />
    <div>
      {eyebrow ? <span className="ai-callout-eyebrow">{eyebrow}</span> : null}
      <p className="ai-callout-body">{children}</p>
    </div>
  </div>
);
AiCallout.propTypes = { eyebrow: PropTypes.string, children: PropTypes.node };

/** A KPI stat tile (Physical activity summary). */
export const KpiTile = ({ label, value, unit, trend, status, tone }) => (
  <div className="kpi-tile">
    <span className="kpi-label">
      {label}
      {trend ? <span className={`kpi-trend is-${trend}`} aria-hidden="true">{trend === "up" ? "▲" : "▼"}</span> : null}
    </span>
    <span className="kpi-value">
      {value}
      {unit ? <span className="kpi-unit"> {unit}</span> : null}
    </span>
    {status ? <span className={`kpi-status tone-${tone || "muted"}`}>{status}</span> : null}
  </div>
);
KpiTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  unit: PropTypes.string,
  trend: PropTypes.oneOf(["up", "down"]),
  status: PropTypes.string,
  tone: PropTypes.string,
};

/** A numeric stepper cell (Years / Months / Days). */
// The DA "NumStepper" (2961:3712): a 50×36 box — centred value + an 18px
// arrow column — with the unit label OUTSIDE the box. Arrow vectors off the
// board's own assets (9.4×5.4, #99a1af).
const StepUp = () => (
  <svg viewBox="0 0 9.4 5.4" fill="none" aria-hidden="true">
    <path d="M0.7 4.7L4.7 0.7L8.7 4.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const StepDown = () => (
  <svg viewBox="0 0 9.4 5.4" fill="none" aria-hidden="true">
    <path d="M0.7 0.7L4.7 4.7L8.7 0.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Stepper = ({ label, value, onChange, max, disabled, nullable }) => {
  // Nullable mode (CC / Dx Duration): null renders as "-" (untouched),
  // arrow up from null lands on 1, empty-typed input clears back to null.
  // Non-nullable callers (hospitalisation, ai-transcript) always feed a
  // number so the null branches never fire — behaviour is unchanged.
  const isEmpty = nullable && (value === null || value === undefined);
  const display = isEmpty ? "-" : String(value).padStart(2, "0");
  const bump = (delta) => {
    if (isEmpty) {
      onChange(delta > 0 ? 1 : 0);
      return;
    }
    const next = Math.min(Math.max((value || 0) + delta, 0), max);
    onChange(next);
  };
  const onType = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw === "" && nullable) {
      onChange(null);
      return;
    }
    const n = Number(raw) || 0;
    onChange(Math.min(n, max));
  };
  return (
    <div className={`cc-stepper${disabled ? " is-disabled" : ""}`}>
      <div className="cc-stepper-box">
        <input
          type="text"
          inputMode="numeric"
          value={display}
          onChange={onType}
          disabled={!!disabled}
          aria-label={label}
        />
        <span className="cc-stepper-arrows">
          <button
            type="button"
            onClick={() => bump(+1)}
            disabled={!!disabled}
            aria-label={`${label} up`}
          >
            <StepUp />
          </button>
          <button
            type="button"
            onClick={() => bump(-1)}
            disabled={!!disabled}
            aria-label={`${label} down`}
          >
            <StepDown />
          </button>
        </span>
      </div>
      <span className="cc-stepper-unit">{label}</span>
    </div>
  );
};
Stepper.propTypes = {
  label: PropTypes.string.isRequired,
  /** Number in [0, max]; null is only permitted when `nullable` is true. */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  onChange: PropTypes.func.isRequired,
  max: PropTypes.number.isRequired,
  /** Greys the box and blocks the field + both arrows. */
  disabled: PropTypes.bool,
  /** When true, null value renders as "-" and empty input clears to null. */
  nullable: PropTypes.bool,
};
Stepper.defaultProps = {
  disabled: false,
  nullable: false,
};

/** A two-option Yes/No toggle; selected pill fills purple@10% (Figma 1982:12683). */
export const YesNo = ({ value, onChange, name }) => (
  <div className="yn-toggle" role="radiogroup" aria-label={name}>
    {[
      ["yes", "Yes"],
      ["no", "No"],
    ].map(([v, lbl]) => (
      <button
        key={v}
        type="button"
        role="radio"
        aria-checked={value === v}
        className={`yn-opt${value === v ? " is-on" : ""}`}
        onClick={() => onChange(v)}
      >
        {lbl}
      </button>
    ))}
  </div>
);
YesNo.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
};

/**
 * A quick-pick group: a label with an optional "Suggested for …" note over a row
 * of add-chips. UI-only — chips are inert placeholders.
 */
export const QuickPick = ({ label, suggestedFor, items, extra }) => (
  <div className="sec-field qp">
    <div className="qp-head">
      <span className="qp-label">{label}</span>
      {suggestedFor ? <span className="qp-note">✦ Suggested for {suggestedFor}</span> : null}
    </div>
    <div className="qp-chips">
      {items.map((it) => (
        <button key={it} type="button" className="qp-chip" title="Not wired up yet">
          <span className="qp-chip-plus">+</span> {it}
        </button>
      ))}
      {extra ? (
        <button type="button" className="qp-chip is-more" title="Not wired up yet">
          + {extra} more
        </button>
      ) : null}
    </div>
  </div>
);
QuickPick.propTypes = {
  label: PropTypes.string.isRequired,
  suggestedFor: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  extra: PropTypes.number,
};
